import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLang } from '../hooks/useLang';
import { supabase } from '../lib/supabase';
import PhoneInput from '../components/PhoneInput';
import './Auth.css';

function passwordStrength(pw) {
  if (!pw) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 8)  score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const levels = [
    { score: 0, label: '',          color: 'transparent' },
    { score: 1, label: 'Very weak', color: '#E8341A' },
    { score: 2, label: 'Weak',      color: '#E8341A' },
    { score: 3, label: 'Fair',      color: '#E8C547' },
    { score: 4, label: 'Good',      color: '#4CAF7D' },
    { score: 5, label: 'Strong',    color: '#4CAF7D' },
  ];
  return levels[Math.min(score, 5)];
}

function SocialBtn({ icon, label, onClick }) {
  return (
    <button type="button" onClick={onClick}
      style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', width:'100%', padding:'11px 16px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'12px', color:'var(--sand)', fontSize:'14px', fontWeight:'600', cursor:'pointer', fontFamily:'inherit' }}
      onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.09)'}
      onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.05)'}
    >
      <span style={{fontSize:'18px'}}>{icon}</span> {label}
    </button>
  );
}

function StepDots({ step, total }) {
  return (
    <div style={{display:'flex', justifyContent:'center', gap:'8px', marginBottom:'28px'}}>
      {Array.from({length: total}).map((_,i) => (
        <div key={i} style={{width: i===step ? '24px' : '8px', height:'8px', borderRadius:'4px', background: i===step ? 'var(--red)' : i<step ? 'rgba(232,52,26,0.4)' : 'rgba(255,255,255,0.12)', transition:'all 0.25s'}} />
      ))}
    </div>
  );
}

const COUNTRIES = ['Germany','Tunisia','France','Belgium','Netherlands','Switzerland','Austria','Sweden','Canada','United States','United Kingdom','Other'];
const LANGUAGES = ['Arabic','French','German','English','Italian','Spanish'];
const GERMAN_CITIES = ['Berlin','Hamburg','Munich','Frankfurt','Cologne','Stuttgart','Düsseldorf','Dortmund','Essen','Leipzig','Bremen','Dresden','Hanover','Nuremberg'];
const TUNISIAN_CITIES = ['Tunis','Sfax','Sousse','Monastir','Bizerte','Gabès','Ariana','Gafsa','Kairouan','Nabeul'];

export default function Register() {
  const [searchParams] = useSearchParams();
  const { signUp } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const [step,     setStep]     = useState(0);
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [showPw,   setShowPw]   = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [phoneExists,  setPhoneExists]  = useState(false);
  const [showPw2,  setShowPw2]  = useState(false);

  const [role,     setRole]     = useState(searchParams.get('role') || 'sender');
  const [fullName, setFullName] = useState('');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [password2,setPassword2]= useState('');
  const [phone,    setPhone]    = useState('');
  const [country,  setCountry]  = useState('Germany');
  const [city,     setCity]     = useState('');
  const [languages,setLanguages]= useState(['Arabic','French']);
  const [terms,    setTerms]    = useState(false);

  const pw    = passwordStrength(password);
  const cities = country === 'Tunisia' ? TUNISIAN_CITIES : country === 'Germany' ? GERMAN_CITIES : [];

  function toggleLang(lang) {
    setLanguages(l => l.includes(lang) ? l.filter(x => x !== lang) : [...l, lang]);
  }

  async function handleOAuth(provider) {
    await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/dashboard` },
    });
  }

  function validateStep1() {
    if (!fullName.trim())       { setError('Please enter your full name.');        return false; }
    if (!email.trim())          { setError('Please enter your email.');            return false; }
    if (password.length < 8)    { setError('Password must be at least 8 characters.'); return false; }
    if (password !== password2) { setError('Passwords do not match.');             return false; }
    return true;
  }

  function next() {
    setError('');
    if (step === 1 && !validateStep1()) return;
    setStep(s => s + 1);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!terms)               { setError('Please accept the Terms & Privacy Policy.'); return; }
    if (phoneExists)          { setError('This phone number is already linked to another account.'); return; }
    if (!validateStep1())     { setStep(1); return; }
    setLoading(true); setError('');

    // Normalize phone — strip all spaces for consistent comparison
    const phoneNormalized = phone ? phone.replace(/\s+/g, '') : '';

    // Check phone uniqueness via RPC — runs SECURITY DEFINER, no auth needed
    if (phoneNormalized) {
      const { data: phoneExistsResult } = await supabase.rpc('check_phone_exists', { p_phone: phoneNormalized });
      if (phoneExistsResult === true) {
        setPhoneExists(true);
        setStep(2);
        setLoading(false);
        return;
      }
    }

    // Pass all profile data in metadata — trigger saves it, no post-signup update needed
    // (no session available when email confirmation is ON)
    const { data, error: signUpErr } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        data: {
          full_name: fullName.trim(),
          role,
          phone:     phoneNormalized || null,
          country:   country      || 'Germany',
          city:      city         || null,
          languages: languages.length > 0 ? JSON.stringify(languages) : null,
        },
      },
    });

    // Supabase doesn't error on duplicate email — it returns user with identities=[]
    if (data?.user && (!data.user.identities || data.user.identities.length === 0)) {
      setEmailExists(true);
      setStep(1);
      setLoading(false);
      return;
    }

    if (signUpErr) {
      const msg = signUpErr.message || '';
      if (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('already exists')) {
        setEmailExists(true);
        setStep(1);
        setError('');
      } else {
        setError(msg);
      }
      setLoading(false);
      return;
    }

    // Record liability agreement via RPC (works without session — email confirmation is ON)
    const userId = data?.user?.id;
    if (userId) {
      await supabase.rpc('record_liability_agreement', {
        p_user_id:   userId,
        p_full_name: fullName.trim(),
        p_role:      role,
        p_version:   'v1.0 — March 2026',
      });
    }

    setLoading(false);
    navigate('/dashboard');
  }

  const roles = [
    { val:'sender',      icon:'📦', title: t('auth.iAmSender'),      desc: t('auth.senderDesc') },
    { val:'transporter', icon:'✈️', title: t('auth.iAmTransporter'), desc: t('auth.transporterDesc') },
    { val:'both',        icon:'🔄', title: t('auth.iAmBoth'),         desc: t('auth.bothDesc') },
  ];

  const headings = ['Join Transportily', 'Create your account', 'Almost done!'];
  const subs     = ['How will you use Transportily?', 'Set up your login credentials', 'Add a few more details'];

  return (
    <div className="page auth-page">
      <div className="auth-card" style={{maxWidth:'480px'}}>
        <div className="auth-header">
          <Link to="/" className="auth-logo">Transport<span>ily</span></Link>
          <h2>{headings[step]}</h2>
          <p style={{color:'var(--olive)', fontSize:'14px', marginTop:'4px'}}>{subs[step]}</p>
        </div>

        <StepDots step={step} total={3} />
        {error && <div className="alert alert-error" style={{marginBottom:'16px'}}>{error}</div>}

        {/* ── STEP 0: Role + Social ── */}
        {step === 0 && (
          <div>
            <div style={{display:'flex', flexDirection:'column', gap:'10px', marginBottom:'20px'}}>
              {roles.map(r => (
                <button key={r.val} type="button" onClick={() => setRole(r.val)}
                  style={{display:'flex', alignItems:'flex-start', gap:'14px', padding:'14px 16px', background: role===r.val ? 'rgba(232,52,26,0.1)' : 'rgba(255,255,255,0.04)', border:`1px solid ${role===r.val ? 'var(--red)' : 'rgba(255,255,255,0.08)'}`, borderRadius:'14px', cursor:'pointer', textAlign:'left', fontFamily:'inherit'}}
                >
                  <span style={{fontSize:'24px', flexShrink:0, marginTop:'2px'}}>{r.icon}</span>
                  <div>
                    <div style={{fontWeight:'700', fontSize:'14px', color:'var(--sand)', marginBottom:'3px'}}>{r.title}</div>
                    <div style={{fontSize:'12px', color:'var(--olive)', lineHeight:'1.5'}}>{r.desc}</div>
                  </div>
                  <div style={{marginLeft:'auto', flexShrink:0}}>
                    <div style={{width:'20px', height:'20px', borderRadius:'50%', border:`2px solid ${role===r.val ? 'var(--red)' : 'rgba(255,255,255,0.2)'}`, display:'flex', alignItems:'center', justifyContent:'center'}}>
                      {role===r.val && <div style={{width:'10px', height:'10px', borderRadius:'50%', background:'var(--red)'}} />}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div style={{display:'flex', flexDirection:'column', gap:'8px', marginBottom:'16px'}}>
              <SocialBtn icon="🔵" label="Continue with Google"   onClick={() => handleOAuth('google')} /></div>

            <div style={{display:'flex', alignItems:'center', gap:'10px', marginBottom:'16px'}}>
              <div style={{flex:1, height:'1px', background:'rgba(255,255,255,0.08)'}} />
              <span style={{color:'var(--olive)', fontSize:'12px', whiteSpace:'nowrap'}}>or create account with email</span>
              <div style={{flex:1, height:'1px', background:'rgba(255,255,255,0.08)'}} />
            </div>

            <button type="button" className="btn btn-primary btn-block" onClick={() => { setError(''); setStep(1); }}>
              Continue with email →
            </button>
            <p className="auth-footer">Already have an account? <Link to="/login">{t('auth.signInLink')}</Link></p>
          </div>
        )}

        {/* ── STEP 1: Account details ── */}
        {step === 1 && (
          <div>
            <div className="form-group">
              <label>{t('auth.fullNameLabel')}</label>
              <input type="text" placeholder="Ahmed Ben Ali" value={fullName} onChange={e => setFullName(e.target.value)} autoComplete="name" />
              <span style={{fontSize:'11px', color:'var(--olive)'}}>Your real name builds trust with other community members</span>
            </div>
            <div className="form-group">
              <label>{t('auth.emailLabel')}</label>
              <input type="email" placeholder="you@example.com" value={email}
                onChange={e => { setEmail(e.target.value); setEmailExists(false); }}
                autoComplete="email"
                style={{ borderColor: emailExists ? 'var(--red)' : undefined }}
              />
              {emailExists && (
                <span style={{fontSize:'11px', color:'var(--red)', fontWeight:'600'}}>
                  ✗ An account with this email already exists.{' '}
                  <a href="/login" style={{color:'var(--red)', textDecoration:'underline'}}>Sign in instead?</a>
                </span>
              )}
            </div>
            <div className="form-group">
              <label>{t('auth.passwordLabel')}</label>
              <div style={{position:'relative'}}>
                <input type={showPw ? 'text' : 'password'} placeholder="At least 8 characters" value={password} onChange={e => setPassword(e.target.value)} style={{paddingRight:'44px'}} />
                <button type="button" onClick={() => setShowPw(v=>!v)} style={{position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--olive)', fontSize:'16px', padding:0}}>
                  {showPw ? '🙈' : '👁️'}
                </button>
              </div>
              {password && (
                <div style={{marginTop:'8px'}}>
                  <div style={{display:'flex', gap:'4px', marginBottom:'4px'}}>
                    {[1,2,3,4,5].map(i => (
                      <div key={i} style={{flex:1, height:'4px', borderRadius:'2px', background: i<=pw.score ? pw.color : 'rgba(255,255,255,0.1)', transition:'background 0.2s'}} />
                    ))}
                  </div>
                  <span style={{fontSize:'11px', color:pw.color, fontWeight:'600'}}>{pw.label}</span>
                  <span style={{fontSize:'11px', color:'var(--olive)', marginLeft:'8px'}}>
                    {pw.score < 3 ? '— add uppercase, numbers or symbols' : pw.score < 5 ? '— almost there!' : '— 🎉 great password'}
                  </span>
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Confirm password</label>
              <div style={{position:'relative'}}>
                <input type={showPw2 ? 'text' : 'password'} placeholder="Repeat your password" value={password2} onChange={e => setPassword2(e.target.value)} style={{paddingRight:'44px', borderColor: password2 && password2 !== password ? 'var(--red)' : password2 && password2 === password ? '#4CAF7D' : undefined}} />
                <button type="button" onClick={() => setShowPw2(v=>!v)} style={{position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--olive)', fontSize:'16px', padding:0}}>
                  {showPw2 ? '🙈' : '👁️'}
                </button>
              </div>
              {password2 && password2 !== password && <span style={{fontSize:'11px', color:'var(--red)'}}>Passwords do not match</span>}
              {password2 && password2 === password && <span style={{fontSize:'11px', color:'#4CAF7D'}}>✓ Passwords match</span>}
            </div>
            <div style={{display:'flex', gap:'10px'}}>
              <button type="button" className="btn btn-ghost" onClick={() => { setError(''); setStep(0); }} style={{flex:1}}>← Back</button>
              <button type="button" className="btn btn-primary" onClick={next} style={{flex:2}}>Continue →</button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Extra details ── */}
        {step === 2 && (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Phone number <span style={{color:'var(--olive)', fontWeight:'400', fontSize:'12px'}}>— recommended</span></label>
              <PhoneInput value={phone} onChange={v => setPhone(v)} />
              {phoneExists && (
                <span style={{fontSize:'11px', color:'var(--red)', fontWeight:'600'}}>
                  ✗ This phone number is already linked to another account.
                </span>
              )}
              {!phoneExists && <span style={{fontSize:'11px', color:'var(--olive)'}}>Used for contacting you and WhatsApp coordination</span>}
            </div>
            <div className="form-group">
              <label>Country you live in</label>
              <select value={country} onChange={e => { setCountry(e.target.value); setCity(''); }}>
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {cities.length > 0 && (
              <div className="form-group">
                <label>City <span style={{color:'var(--olive)', fontWeight:'400', fontSize:'12px'}}>— optional</span></label>
                <select value={city} onChange={e => setCity(e.target.value)}>
                  <option value="">Select city</option>
                  {cities.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
            <div className="form-group">
              <label>Languages spoken</label>
              <div style={{display:'flex', flexWrap:'wrap', gap:'8px', marginTop:'4px'}}>
                {LANGUAGES.map(lang => (
                  <button key={lang} type="button" onClick={() => toggleLang(lang)}
                    style={{padding:'6px 14px', borderRadius:'100px', fontSize:'13px', border:`1px solid ${languages.includes(lang) ? 'var(--red)' : 'rgba(255,255,255,0.1)'}`, background: languages.includes(lang) ? 'rgba(232,52,26,0.12)' : 'rgba(255,255,255,0.04)', color: languages.includes(lang) ? 'var(--sand)' : 'var(--olive)', cursor:'pointer', fontFamily:'inherit'}}>
                    {lang}
                  </button>
                ))}
              </div>
              <span style={{fontSize:'11px', color:'var(--olive)', marginTop:'6px', display:'block'}}>Helps senders find transporters who speak their language</span>
            </div>

            <label style={{display:'flex', alignItems:'flex-start', gap:'12px', cursor:'pointer', marginBottom:'20px', padding:'12px 14px', background: terms ? 'rgba(76,175,125,0.06)' : 'rgba(255,255,255,0.03)', border:`1px solid ${terms ? 'rgba(76,175,125,0.25)' : 'rgba(255,255,255,0.08)'}`, borderRadius:'12px'}}>
              <div onClick={() => setTerms(v=>!v)}
                style={{width:'20px', height:'20px', borderRadius:'6px', border:`2px solid ${terms ? '#4CAF7D' : 'rgba(255,255,255,0.25)'}`, background: terms ? '#4CAF7D' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:'1px', cursor:'pointer'}}>
                {terms && <span style={{color:'#fff', fontSize:'12px', lineHeight:1}}>✓</span>}
              </div>
              <span style={{fontSize:'13px', color:'var(--olive)', lineHeight:'1.5'}}>
                I agree to the <a href="/terms" target="_blank" style={{color:'var(--sand)'}}>Terms of Service</a> and <a href="/privacy" target="_blank" style={{color:'var(--sand)'}}>Privacy Policy</a>. I understand Transportily is a community platform and I am responsible for accurate declarations.
              </span>
            </label>

            <div style={{display:'flex', gap:'10px'}}>
              <button type="button" className="btn btn-ghost" onClick={() => { setError(''); setStep(1); }} style={{flex:1}}>← Back</button>
              <button type="submit" className="btn btn-primary" style={{flex:2}} disabled={loading}>
                {loading ? '⏳ Creating account...' : '🎉 Create account'}
              </button>
            </div>
            <p className="auth-footer" style={{marginTop:'16px'}}>Already have an account? <Link to="/login">{t('auth.signInLink')}</Link></p>
          </form>
        )}
      </div>
    </div>
  );
}